import { ColDef, PostSortRowsParams } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useMemo, useRef, useState } from "react";
import { aggridHeaderClass } from "src/components/common/common";
import useUiuxStore from "src/store/useUiuxStore";
import { CreateCellClickHandler, createCellCssClassRules, CreateCellDoubleClickHandler, createGridDropDownCommitAfter, CustomCellClickAction, CustomCellDoubleClickAction, GridCellConfig, InsertRowHandler, postSortHandler, RemoveRowHandler, useCellChangeHandler, useRowSelectionHandler } from "src/utils/grid/GridHandlerContent";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { defaultColDef, getDefaultRowIndexColumnConfig, getRowStyleFlags } from "src/utils/grid/gridStyleUtils";
import { usePRD3010_R01Store } from "./Store_PRD3010_R01";

import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";
import { PRD3010_R01InspectDto } from "./Dto_PRD3010_R01";

import Grid_Excel from "src/components/common/gridWrapper/Grid_Excel";

// 범용 훅 임포트
import { useDetailTableEvents } from "src/hooks/detail-table/useDetailTableEvents";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { CommonCodeColumn, getCommonCodeColumnOptions } from "src/utils/grid/GridColumnContent";


const COLUMN_STATE_KEY = 'PRD3010_R01-inspect-grid-column-state';

interface Props {
	gridRef: React.RefObject<AgGridReact<PRD3010_R01InspectDto> | null>;
	defaultPRD3010_R01InspectRow: PRD3010_R01InspectDto;
}

export const InspectTable_PRD3010_R01 = (props: Props) => {

	const { gridRef, defaultPRD3010_R01InspectRow } = props;

	const { 
			PRD3010_R01InspectList, setPRD3010_R01InspectList, 
			editPRD3010_R01,
			selectedPRD3010_R01InspectList, setSelectedPRD3010_R01InspectList, removeSelectedPRD3010_R01InspectList, addSelectedPRD3010_R01InspectList, initSelectedPRD3010_R01InspectList,
			deletedPRD3010_R01InspectList, setDeletedPRD3010_R01InspectList, addDeletedPRD3010_R01InspectList, removeDeletedPRD3010_R01InspectList,
			insertPRD3010_R01InspectList, updatePRD3010_R01InspectList, removePRD3010_R01InspectList,
			originalPRD3010_R01InspectList, setOriginalPRD3010_R01InspectList,
			inspectColumnState, setInspectColumnState,
			inspectScrollTop, setInspectScrollTop,
		} = usePRD3010_R01Store();
	const { setCodeSearchModalProps } = useUiuxStore();
	const { faultyValueList } = useCommonCodeStore();
	const [clickable, setClickable] = useState(true);

	//const gridRef = useRef<AgGridReact>(null);
	const gridWrapperRef = useRef<HTMLDivElement>(null); // 1. div를 위한 ref 생성

	const {
		getRowId,
		createRowClassRules,
		createCellClassRules,
		paddedGridData,
		renderIndexCell,
	} = useGridSettingContent({
		gridRef,
		key: 'valueIndex',
		gridData: PRD3010_R01InspectList,
		defaultGridData: defaultPRD3010_R01InspectRow,
		selectedRow: selectedPRD3010_R01InspectList,
		originalGridData: originalPRD3010_R01InspectList,
		updateRow: (index: number, data: any) => updatePRD3010_R01InspectList(index, data),
	});

	const cssCellConfig = useMemo<GridCellConfig>(() => ({
	disabledCells: [''],
	insertedDisabledCells: [''],
	commonDisabledCells: ['', ],
	
	pointerCells: ['badCase', 'badQty'],
	insertedPointerCells: [''],
	commonPointerCells: [''],
	
	alignLeftCells: ['warehouseCd', 'compCd']
}), []);

	const headerNames: Record<string, string> = {
		rowIndex: "",
		badCase: '불량사유',
		badQty: '불량수량',
	}
	const {
		disabledInsertedColumns, disabledColumns,
		pointerInsertedColumns, pointerColumns,
		alignLeftColumns,
	} = useMemo(() => createCellCssClassRules(cssCellConfig), [cssCellConfig]);

	const cellCssDefaultColDef = useMemo(() => {
        const baseRules = createCellClassRules<PRD3010_R01InspectDto>({
            idKey: 'valueIndex',
            selectedList: selectedPRD3010_R01InspectList,
            deletedList: deletedPRD3010_R01InspectList,
            disabledInsertedColumns,	disabledColumns,
            pointerInsertedColumns,		pointerColumns,
            alignLeftColumns,
        });

        return { ...defaultColDef, cellClassRules: { ...baseRules, }}; 
	}, [
        selectedPRD3010_R01InspectList, deletedPRD3010_R01InspectList,
        disabledInsertedColumns, disabledColumns,         
        pointerInsertedColumns, pointerColumns,
		alignLeftColumns,
    ]);

	const commonInputColumns = ['badCase', 'badQty'];

	// 범용 이벤트 훅을 위한 설정
	const defaultEditableColumns = ['badCase', 'badQty'];

	const handleGridDropDownCommitAfter = useMemo(() => 
		createGridDropDownCommitAfter<PRD3010_R01InspectDto>(defaultEditableColumns),
	[defaultEditableColumns],);

	const baseColumnDefs: ColDef<PRD3010_R01InspectDto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(
			(params: PRD3010_R01InspectDto) => renderIndexCell(
				params, 
				(innerParams: PRD3010_R01InspectDto) => getRowStyleFlags (
					innerParams,
					{
						deletedList: deletedPRD3010_R01InspectList,
						selectedList: selectedPRD3010_R01InspectList,
						dataList: PRD3010_R01InspectList
					}),
				(rowIndex: number, e: React.MouseEvent) => InsertRowHandler({
					event: e,
					dataList: PRD3010_R01InspectList,
					rowData: PRD3010_R01InspectList[rowIndex],
					insertRow: insertPRD3010_R01InspectList,
					defaultRowData: defaultPRD3010_R01InspectRow,
					setClickable
				}),
				(rowData) => RemoveRowHandler( 
					gridRef,
					PRD3010_R01InspectList, 
					rowData,  // rowData는 renderIndexCell에서 Dto 형식으로 선언함
					setClickable, 
					removePRD3010_R01InspectList, 
					(valueIndex) => addDeletedPRD3010_R01InspectList([valueIndex.toString()])
				),
			)
		),
        {
            headerName: '불량사유', colId: 'badCase', field: 'badCase', width: 180,
			...CommonCodeColumn.options(faultyValueList, true, CommonCodeColumn.change(updatePRD3010_R01InspectList), handleGridDropDownCommitAfter),
		},
        {
            headerName: '불량수량', colId: 'badQty', field: 'badQty', width: 180,
			editable: true,
		},
		// ...IndexParameterCell(),
	], [
		deletedPRD3010_R01InspectList, PRD3010_R01InspectList, faultyValueList, updatePRD3010_R01InspectList,
	]);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, []),
	[baseColumnDefs]);

	const columnDefs: ColDef<PRD3010_R01InspectDto>[] = useMemo(() => {
		if (inspectColumnState === null) {
			return baseColumnDefs;
		}
		
		return GridCustomColumnDefs<PRD3010_R01InspectDto>({
			baseColumnDefs,
			columnState: inspectColumnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass
		});
	}, [baseColumnDefs, inspectColumnState, headerNames]);

	
	const handleCellChange = useCellChangeHandler({
		dataList: PRD3010_R01InspectList,
		originalDataList: originalPRD3010_R01InspectList,
		updateRow: updatePRD3010_R01InspectList,
		customBeforeUpdate: (updatedData, field, value) => {
			return updatedData;
		},
	});
	

	const handleRowClick = useRowSelectionHandler({
		dataList: PRD3010_R01InspectList,
		idKey: 'valueIndex',
		selectedList: selectedPRD3010_R01InspectList,
		deletedList: deletedPRD3010_R01InspectList,
		addSelected: addSelectedPRD3010_R01InspectList,
		removeSelected: removeSelectedPRD3010_R01InspectList,
		clickable, setClickable
	});


	// 범용 이벤트 훅 사용 (코드 검색 없이) - 방향키 이동 기능 유지
	/**
	 * 기본 상세 테이블 이벤트 핸들러 훅
	 * 
	 * 주요 기능:
	 * - 키다운 네비게이션 (방향키 이동)
	 * - 셀 클릭/키다운 이벤트 처리
	 * 
	 * @param detailList - 상세 데이터 목록
	 * @param editableColumns - 편집 가능한 컬럼 목록
	 */
	const cellEventHandlers = useDetailTableEvents({
		detailList: PRD3010_R01InspectList as any,
		editableColumns: defaultEditableColumns,
		keyDownConfig: {
			enableArrowNavigation: true,
		}
	});
	
	const cellClickActions: CustomCellClickAction<PRD3010_R01InspectDto>[] = useMemo(() => [
		{
			validate: (field, data) => {
				// 입고량과 비고는 편집 가능한 상태일 때만 클릭으로 편집 시작
				if (field === 'updateTransportQty' || field === 'remark') {
					return commonInputColumns.includes(field);
				}
				return commonInputColumns.includes(field);
			},
			//aggrid 기본 편집 기능
			action: (e) => { e.api.startEditingCell({ rowIndex: e.rowIndex!, colKey: e.colDef.field! }); }
		},
	], []);
	const handleCellClick = CreateCellClickHandler(PRD3010_R01InspectList, 'valueIndex', cellClickActions);

	//더블클릭 액션 없음
	const cellDoubleClickActions: CustomCellDoubleClickAction<PRD3010_R01InspectDto>[] = useMemo(() => [
	], [editPRD3010_R01?.itemCd]);
	const handleCellDoubleClick = CreateCellDoubleClickHandler(PRD3010_R01InspectList, 'valueIndex', cellDoubleClickActions);


	const handlePostSort = useCallback((params: PostSortRowsParams<PRD3010_R01InspectDto>) => {
		postSortHandler(params, PRD3010_R01InspectList, originalPRD3010_R01InspectList, setPRD3010_R01InspectList);
	}, [PRD3010_R01InspectList, originalPRD3010_R01InspectList, ]);

	const pinnedBottomRowData = useMemo(() => {

		const totalQty : number = PRD3010_R01InspectList
			.reduce((sum, item) => sum + (deletedPRD3010_R01InspectList.includes(item.itemCd) ? 0 : Number(item.badQty) || 0), 0);

		return [{
			badCaseNm: PRD3010_R01InspectList.length.toLocaleString() + "건",
			badQty: totalQty,
		}];	  
	}, [PRD3010_R01InspectList, deletedPRD3010_R01InspectList,]);

	return (
		<>			
			<GridContextWrapper<PRD3010_R01InspectDto>
				enableCellCalculation
				gridWrapperRef={gridWrapperRef}
				gridRef={gridRef}
				paddedGridData={paddedGridData}
				setPaddedGridData={setPRD3010_R01InspectList}
				initSelected={initSelectedPRD3010_R01InspectList}
				defaultRowData={defaultPRD3010_R01InspectRow}
				baseColumnDefs={inspectColumnState ? columnDefs : baseColumnDefs}
			defaultColumnState={defaultColumnState}	
				setColumnState={setInspectColumnState}
				localStorageKey={COLUMN_STATE_KEY}
				menuItems={undefined}
			>
				<Grid_Excel<PRD3010_R01InspectDto>
					ref={gridRef}
					rowData={paddedGridData}	getRowId={getRowId} // +- 버튼 테이블 getRowId는 valueIndex로 고정
					columnDefs={columnDefs}		
					defaultColDef={cellCssDefaultColDef}

					rowClassRules={createRowClassRules<PRD3010_R01InspectDto>({
						idKey: 'valueIndex',
						selectedList: selectedPRD3010_R01InspectList,
						deletedList: deletedPRD3010_R01InspectList
					})}

					onRowClicked={handleRowClick}
					onCellClicked={handleCellClick}
					onCellDoubleClicked={handleCellDoubleClick}
					onCellKeyDown={cellEventHandlers.handleCellKeyDown}
					onCellValueChanged={handleCellChange}
					pinnedBottomRowData={pinnedBottomRowData}
					

					onPostSortRows={handlePostSort} //onSortChanged 삭제, 커스텀 정렬 기능추가 
					// Grid_Select에 공통 로직 처리를 위한 props 전달
					columnStateKey={COLUMN_STATE_KEY}			//컬럼 상태 키(로컬스토리지)	
					setColumnState={setInspectColumnState}		//컬럼 상태 저장
					getDefaultColumnState={() => defaultColumnState}
					scrollTop={inspectScrollTop}
					setScrollTop={setInspectScrollTop}
				/>
			</GridContextWrapper>
		</>
	)
}
